/**
 * Video Recorder Hook - DUAL FILE RECORDING FOR SERVER-SIDE MERGING
 *
 * Records TWO separate files:
 * 1. Video file: Camera + user microphone
 * 2. Audio file: AI Journalist responses only
 *
 * Both files are uploaded to the server where they are merged using ffmpeg.
 */

import { useRef, useState } from 'react';

export function useVideoRecorder() {
  const videoRecorderRef = useRef<MediaRecorder | null>(null);
  const aiAudioRecorderRef = useRef<MediaRecorder | null>(null);
  const videoChunksRef = useRef<Blob[]>([]);
  const aiAudioChunksRef = useRef<Blob[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const aiAudioDestinationRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const silentSourceRef = useRef<ConstantSourceNode | null>(null);

  const [isRecording, setIsRecording] = useState(false);
  const [recordingError, setRecordingError] = useState<string | null>(null);

  /**
   * Start recording - creates TWO separate recorders
   * @param videoStream - MediaStream with video and user microphone
   * @param audioContext - AudioContext for capturing AI audio
   */
  const startRecording = (videoStream: MediaStream, audioContext: AudioContext) => {
    try {
      console.log('[VideoRecorder] Starting dual-file recording (server-side merging)...');

      // Stop any existing recording first to prevent multiple recorders
      if (videoRecorderRef.current && videoRecorderRef.current.state !== 'inactive') {
        console.log('[VideoRecorder] Stopping existing video recorder before starting new one');
        videoRecorderRef.current.stop();
        videoRecorderRef.current = null;
      }
      if (aiAudioRecorderRef.current && aiAudioRecorderRef.current.state !== 'inactive') {
        console.log('[VideoRecorder] Stopping existing AI audio recorder before starting new one');
        aiAudioRecorderRef.current.stop();
        aiAudioRecorderRef.current = null;
      }
      if (silentSourceRef.current) {
        try {
          silentSourceRef.current.stop();
          silentSourceRef.current = null;
        } catch (e) { /* ignore */ }
      }

      // Clear any leftover chunks from previous session
      videoChunksRef.current = [];
      aiAudioChunksRef.current = [];

      // Check browser support
      if (!MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')) {
        throw new Error('Video recording not supported in this browser');
      }

      audioContextRef.current = audioContext;

      // 1. Create video recorder (camera + user microphone)
      const videoRecorder = new MediaRecorder(videoStream, {
        mimeType: 'video/webm;codecs=vp8,opus',
        videoBitsPerSecond: 2500000 // 2.5 Mbps for good quality
      });

      videoRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          videoChunksRef.current.push(event.data);
          console.log('[VideoRecorder] Video chunk:', event.data.size, 'bytes');
        }
      };

      videoRecorder.onerror = (event: any) => {
        console.error('[VideoRecorder] Video recording error:', event.error);
        setRecordingError('Video recording failed: ' + event.error);
      };

      videoRecorder.onstop = () => {
        console.log('[VideoRecorder] Video recording stopped. Total chunks:', videoChunksRef.current.length);
      };

      // 2. Create AI audio destination and recorder
      const aiDestination = audioContext.createMediaStreamDestination();
      aiAudioDestinationRef.current = aiDestination;

      // Create a silent source that feeds continuous silence to maintain timeline sync
      // This ensures the AI audio recording matches the video duration exactly
      const silentSource = audioContext.createConstantSource();
      silentSource.offset.value = 0; // Silence (constant value of 0)
      silentSource.connect(aiDestination);
      silentSource.start();
      silentSourceRef.current = silentSource;

      console.log('[VideoRecorder] Silent source created and connected to maintain timeline sync');

      const aiAudioRecorder = new MediaRecorder(aiDestination.stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      aiAudioRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          aiAudioChunksRef.current.push(event.data);
          console.log('[VideoRecorder] AI audio chunk:', event.data.size, 'bytes');
        }
      };

      aiAudioRecorder.onerror = (event: any) => {
        console.error('[VideoRecorder] AI audio recording error:', event.error);
        setRecordingError('AI audio recording failed: ' + event.error);
      };

      aiAudioRecorder.onstop = () => {
        console.log('[VideoRecorder] AI audio recording stopped. Total chunks:', aiAudioChunksRef.current.length);
      };

      // Store refs
      videoRecorderRef.current = videoRecorder;
      aiAudioRecorderRef.current = aiAudioRecorder;

      // Start both recorders
      videoRecorder.start(1000); // Collect data every second
      aiAudioRecorder.start(1000);

      setIsRecording(true);
      setRecordingError(null);

      console.log('[VideoRecorder] Dual recording started (video+mic, AI audio separate)');
    } catch (error) {
      console.error('[VideoRecorder] Failed to start recording:', error);
      setRecordingError(error instanceof Error ? error.message : 'Failed to start recording');
    }
  };

  /**
   * Get reference to AI audio destination for connecting audio sources
   * AI audio chunks should be connected to this destination while being played
   */
  const getAudioDestination = (): MediaStreamAudioDestinationNode | null => {
    return aiAudioDestinationRef.current;
  };

  /**
   * Stop recording and return both video and AI audio blobs
   */
  const stopRecording = (): Promise<{ videoBlob: Blob | null; aiAudioBlob: Blob | null }> => {
    return new Promise((resolve) => {
      const videoRecorder = videoRecorderRef.current;
      const aiAudioRecorder = aiAudioRecorderRef.current;

      if (!videoRecorder || videoRecorder.state === 'inactive') {
        console.warn('[VideoRecorder] No active video recording to stop');
        resolve({ videoBlob: null, aiAudioBlob: null });
        return;
      }

      let videoBlob: Blob | null = null;
      let aiAudioBlob: Blob | null = null;
      let videoStopped = false;
      let audioStopped = false;

      const checkComplete = () => {
        if (videoStopped && audioStopped) {
          console.log('[VideoRecorder] Both recordings finalized');
          resolve({ videoBlob, aiAudioBlob });
        }
      };

      // Stop video recorder
      videoRecorder.onstop = () => {
        console.log('[VideoRecorder] Finalizing video recording...');

        if (videoChunksRef.current.length > 0) {
          videoBlob = new Blob(videoChunksRef.current, { type: 'video/webm' });
          console.log('[VideoRecorder] Video blob created:', videoBlob.size, 'bytes (camera + user mic)');
        } else {
          console.warn('[VideoRecorder] No video chunks recorded');
        }

        videoStopped = true;
        checkComplete();
      };

      // Stop AI audio recorder
      aiAudioRecorder.onstop = () => {
        console.log('[VideoRecorder] Finalizing AI audio recording...');

        if (aiAudioChunksRef.current.length > 0) {
          aiAudioBlob = new Blob(aiAudioChunksRef.current, { type: 'audio/webm' });
          console.log('[VideoRecorder] AI audio blob created:', aiAudioBlob.size, 'bytes (AI journalist only)');
        } else {
          console.warn('[VideoRecorder] No AI audio chunks recorded');
        }

        audioStopped = true;
        checkComplete();
      };

      // Stop silent source
      if (silentSourceRef.current) {
        try {
          silentSourceRef.current.stop();
          silentSourceRef.current = null;
          console.log('[VideoRecorder] Silent source stopped');
        } catch (err) {
          console.warn('[VideoRecorder] Error stopping silent source:', err);
        }
      }

      // Stop both recorders
      videoRecorder.stop();
      if (aiAudioRecorder && aiAudioRecorder.state !== 'inactive') {
        aiAudioRecorder.stop();
      } else {
        // AI audio recorder already stopped or doesn't exist
        audioStopped = true;
        checkComplete();
      }

      setIsRecording(false);
    });
  };

  /**
   * Upload video and AI audio to server for merging
   */
  const uploadVideo = async (
    videoBlob: Blob,
    aiAudioBlob: Blob | null,
    token: string,
    interviewId: string
  ): Promise<void> => {
    try {
      console.log('[VideoRecorder] Uploading separate files for server-side merging...', {
        videoSize: videoBlob.size,
        aiAudioSize: aiAudioBlob?.size || 0,
        hasBothFiles: !!aiAudioBlob
      });

      const formData = new FormData();
      formData.append('video', videoBlob, `interview-${interviewId}-video-${Date.now()}.webm`);
      formData.append('duration', Math.round(videoBlob.size / 250000).toString()); // Rough estimate

      // Upload AI audio if available
      if (aiAudioBlob) {
        formData.append('aiAudio', aiAudioBlob, `interview-${interviewId}-ai-${Date.now()}.webm`);
      } else {
        console.warn('[VideoRecorder] No AI audio blob - video will be uploaded without merging');
      }

      const response = await fetch(`/api/expert/interview/upload-video?token=${encodeURIComponent(token)}`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to upload video');
      }

      const result = await response.json();
      console.log('[VideoRecorder] Upload successful:', result);
    } catch (error) {
      console.error('[VideoRecorder] Upload failed:', error);
      throw error;
    }
  };

  return {
    startRecording,
    stopRecording,
    uploadVideo,
    getAudioDestination,
    isRecording,
    recordingError
  };
}
